import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
	dataService,
	type QueryOptions,
	type MessageQuery,
	type EmailTemplate,
	type InboxReplyTemplate,
	type NeedsInfoSendBody,
	type BoardApprovalSendBody,
	type ApplicationAttachmentUpload,
	type NewApplicationBody,
	type LabelRegistry,
	type InboxGroups,
	type InboxMessageDetail,
	type ExteriorApplicationDetail,
	type ApplicationListQuery,
	type ReplyAttachmentRef,
} from '@/services/data-service';
import type {
	WorkOrder,
	Association,
	TimeEntry,
	Material,
	MaterialCost,
	MiscCost,
	Note,
	Attachment,
	LocationEvent,
	InspectionReport,
	User,
	Task,
	FollowUp,
	CalendarSource,
	// The ARC ruleset CONFIG survives the message refactor — only the records went.
	ApplicationRequestType,
} from '@/types/models';
import type { MessageStatus } from '@/utils/inbox-triage';

// Query key factories
export const queryKeys = {
	workOrders: {
		all: ['workOrders'] as const,
		lists: () => [...queryKeys.workOrders.all, 'list'] as const,
		list: (options: QueryOptions) => [...queryKeys.workOrders.lists(), options] as const,
		details: () => [...queryKeys.workOrders.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.workOrders.details(), id] as const,
	},
	associations: {
		all: ['associations'] as const,
		lists: () => [...queryKeys.associations.all, 'list'] as const,
		list: (options: QueryOptions) => [...queryKeys.associations.lists(), options] as const,
		details: () => [...queryKeys.associations.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.associations.details(), id] as const,
	},
	users: {
		all: ['users'] as const,
		lists: () => [...queryKeys.users.all, 'list'] as const,
		list: (options: QueryOptions) => [...queryKeys.users.lists(), options] as const,
		details: () => [...queryKeys.users.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.users.details(), id] as const,
	},
	timeEntries: {
		all: ['timeEntries'] as const,
		lists: () => [...queryKeys.timeEntries.all, 'list'] as const,
		list: (workOrderId?: string, options?: QueryOptions) => [...queryKeys.timeEntries.lists(), { workOrderId, ...options }] as const,
		details: () => [...queryKeys.timeEntries.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.timeEntries.details(), id] as const,
	},
	materials: {
		all: ['materials'] as const,
		lists: () => [...queryKeys.materials.all, 'list'] as const,
		list: (options: QueryOptions) => [...queryKeys.materials.lists(), options] as const,
		details: () => [...queryKeys.materials.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.materials.details(), id] as const,
	},
	materialCosts: {
		all: ['materialCosts'] as const,
		lists: () => [...queryKeys.materialCosts.all, 'list'] as const,
		list: (workOrderId?: string, options?: QueryOptions) => [...queryKeys.materialCosts.lists(), { workOrderId, ...options }] as const,
		details: () => [...queryKeys.materialCosts.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.materialCosts.details(), id] as const,
	},
	miscCosts: {
		all: ['miscCosts'] as const,
		lists: () => [...queryKeys.miscCosts.all, 'list'] as const,
		list: (workOrderId?: string, options?: QueryOptions) => [...queryKeys.miscCosts.lists(), { workOrderId, ...options }] as const,
		details: () => [...queryKeys.miscCosts.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.miscCosts.details(), id] as const,
	},
	notes: {
		all: ['notes'] as const,
		lists: () => [...queryKeys.notes.all, 'list'] as const,
		list: (workOrderId?: string, options?: QueryOptions) => [...queryKeys.notes.lists(), { workOrderId, ...options }] as const,
		details: () => [...queryKeys.notes.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.notes.details(), id] as const,
	},
	attachments: {
		all: ['attachments'] as const,
		lists: () => [...queryKeys.attachments.all, 'list'] as const,
		list: (workOrderId?: string, inspectionReportId?: string, options?: QueryOptions) =>
			[...queryKeys.attachments.lists(), { workOrderId, inspectionReportId, ...options }] as const,
		details: () => [...queryKeys.attachments.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.attachments.details(), id] as const,
	},
	locationEvents: {
		all: ['locationEvents'] as const,
		lists: () => [...queryKeys.locationEvents.all, 'list'] as const,
		list: (userId?: string, workOrderId?: string, options?: QueryOptions) =>
			[...queryKeys.locationEvents.lists(), { userId, workOrderId, ...options }] as const,
		details: () => [...queryKeys.locationEvents.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.locationEvents.details(), id] as const,
	},
	inspectionReports: {
		all: ['inspectionReports'] as const,
		lists: () => [...queryKeys.inspectionReports.all, 'list'] as const,
		list: (associationId?: string, options?: QueryOptions) =>
			[...queryKeys.inspectionReports.lists(), { associationId, ...options }] as const,
		details: () => [...queryKeys.inspectionReports.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.inspectionReports.details(), id] as const,
	},
	inspectionIssues: {
		all: ['inspectionIssues'] as const,
		lists: () => [...queryKeys.inspectionIssues.all, 'list'] as const,
		list: (options: QueryOptions) => [...queryKeys.inspectionIssues.lists(), options] as const,
		details: () => [...queryKeys.inspectionIssues.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.inspectionIssues.details(), id] as const,
	},
	inspectionReportIssues: {
		all: ['inspectionReportIssues'] as const,
		lists: () => [...queryKeys.inspectionReportIssues.all, 'list'] as const,
		list: (inspectionReportId?: string, options?: QueryOptions) =>
			[...queryKeys.inspectionReportIssues.lists(), { inspectionReportId, ...options }] as const,
		details: () => [...queryKeys.inspectionReportIssues.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.inspectionReportIssues.details(), id] as const,
	},
	locationAnalytics: {
		all: ['locationAnalytics'] as const,
		lists: () => [...queryKeys.locationAnalytics.all, 'list'] as const,
		list: (options: QueryOptions) => [...queryKeys.locationAnalytics.lists(), options] as const,
	},
	tasks: {
		all: ['tasks'] as const,
		lists: () => [...queryKeys.tasks.all, 'list'] as const,
		list: (options: QueryOptions) => [...queryKeys.tasks.lists(), options] as const,
		details: () => [...queryKeys.tasks.all, 'detail'] as const,
		detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
	},
	followUps: {
		all: ['followUps'] as const,
		forWorkOrder: (workOrderId: string) => [...queryKeys.followUps.all, 'workOrder', workOrderId] as const,
	},
	calendarSources: {
		all: ['calendarSources'] as const,
	},
	calendarEvents: {
		all: ['calendarEvents'] as const,
		list: (timeMin: number, timeMax: number) => [...['calendarEvents'], 'list', timeMin, timeMax] as const,
	},
	/**
	 * The unified inbox (#369). `list` keys on the whole query object so two different
	 * filters cache separately; callers must MEMOIZE that object or the key churns every
	 * render and refetches in a loop.
	 */
	/** Improvement requests — the workflow that sits on top of a message (#369). */
	applications: {
		all: ['applications'] as const,
		counts: () => [...queryKeys.applications.all, 'counts'] as const,
		approvers: () => [...queryKeys.applications.all, 'approvers'] as const,
		list: (query: Record<string, unknown>) => [...queryKeys.applications.all, 'list', query] as const,
		/**
		 * Under `'list'` on purpose: `useMergeApplication` refreshes `[...all, 'list']` rather
		 * than `all`, and a paged list must refresh with the single-page ones.
		 */
		pages: (query: Record<string, unknown>) => [...queryKeys.applications.all, 'list', 'pages', query] as const,
		detail: (id: string) => [...queryKeys.applications.all, 'detail', id] as const,
		requestTypes: (associationId: string) => [...queryKeys.applications.all, 'requestTypes', associationId] as const,
		needsInfoPreview: (id: string) => [...queryKeys.applications.all, 'needsInfoPreview', id] as const,
		boardApprovalPreview: (id: string) => [...queryKeys.applications.all, 'boardApprovalPreview', id] as const,
	},
	messages: {
		all: ['messages'] as const,
		lists: () => [...queryKeys.messages.all, 'list'] as const,
		list: (query: MessageQuery) => [...queryKeys.messages.lists(), query] as const,
		/** Under `lists()` (and so `all`): every message write refreshes a paged list too. */
		pages: (query: MessageQuery) => [...queryKeys.messages.lists(), 'pages', query] as const,
		detail: (id: string) => [...queryKeys.messages.all, 'detail', id] as const,
		labels: () => [...queryKeys.messages.all, 'labels'] as const,
		groups: () => [...queryKeys.messages.all, 'groups'] as const,
	},
	applicationRulesets: {
		all: ['applicationRulesets'] as const,
		detail: () => [...queryKeys.applicationRulesets.all, 'detail'] as const,
	},
	emailTemplates: {
		all: ['emailTemplates'] as const,
		list: (kind?: string) => [...queryKeys.emailTemplates.all, 'list', kind ?? 'all'] as const,
	},
	inboxReplyTemplates: {
		all: ['inboxReplyTemplates'] as const,
		list: () => [...queryKeys.inboxReplyTemplates.all, 'list'] as const,
	},
	/**
	 * Approved work orders becoming bills — the Needs Billing badge.
	 *
	 * The only accounting key in this registry, and here rather than inline in
	 * `use-accounting.ts` because `use-live-refresh` needs it and importing that module
	 * drags in the whole accounting service graph. Every other accounting key is built
	 * inline there, where nothing outside needs to name it.
	 */
	workOrderBilling: {
		all: ['accounting', 'work-order-billing'] as const,
		queue: (params: Record<string, unknown>) => [...queryKeys.workOrderBilling.all, 'queue', params] as const,
		preview: (workOrderId?: string) => [...queryKeys.workOrderBilling.all, 'preview', workOrderId] as const,
	},
	/**
	 * Roots of two drawer badges (Ownership Transfers, Review Requests). The full key
	 * factories live in their own hooks and build on these; only the roots are here, for the
	 * same reason as `workOrderBilling` — `use-live-refresh` needs them, and
	 * `use-ownership-transfers` pulls in the accounting service graph.
	 */
	ownershipTransfers: {
		all: ['ownership-transfers'] as const,
		/** The badge alone — NOT the root, which also holds the paginated account-options picker. */
		pendingCount: () => ['ownership-transfers', 'pending-count'] as const,
	},
	reviewRequests: {
		all: ['review-requests'] as const,
		/** The badge alone — the root also holds lists, details and live previews. */
		counts: () => ['review-requests', 'counts'] as const,
	},
};

// Work Orders
export function useWorkOrders(options: QueryOptions & { enabled?: boolean } = {}) {
	const { enabled, ...queryOptions } = options;
	return useQuery({
		queryKey: queryKeys.workOrders.list(queryOptions),
		queryFn: () => dataService.getWorkOrders(queryOptions),
		enabled: enabled !== false,
	});
}

export function useWorkOrder(id: string) {
	return useQuery({
		queryKey: queryKeys.workOrders.detail(id),
		queryFn: () => dataService.getWorkOrder(id),
		enabled: !!id,
	});
}

export function useCreateWorkOrder() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<WorkOrder>) => dataService.createWorkOrder(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
		},
	});
}

export function useUpdateWorkOrder() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<WorkOrder> }) =>
			dataService.updateWorkOrder(id, data),
		onMutate: async ({ id, data }) => {
			await queryClient.cancelQueries({ queryKey: queryKeys.workOrders.detail(id) });
			const previousData = queryClient.getQueryData(queryKeys.workOrders.detail(id));
			queryClient.setQueryData(queryKeys.workOrders.detail(id), (old: WorkOrder) =>
				old ? { ...old, ...data } : old,
			);
			return { previousData };
		},
		onError: (err, { id }, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(queryKeys.workOrders.detail(id), context.previousData);
			}
		},
		onSettled: (data, error, { id }) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
		},
	});
}

export function useDeleteWorkOrder() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteWorkOrder(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
		},
	});
}

// Associations
export function useAssociations(options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.associations.list(options),
		queryFn: () => dataService.getAssociations(options),
		staleTime: 5 * 60 * 1000, // 5 minutes - associations don't change frequently
		gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
		retry: (failureCount, error) => {
			console.log('useAssociations query failed:', error);
			// Don't retry on auth errors or client errors
			if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
				return false;
			}
			return failureCount < 2;
		},
		throwOnError: false,
	});
}

export function useAssociation(id: string) {
	return useQuery({
		queryKey: queryKeys.associations.detail(id),
		queryFn: () => dataService.getAssociation(id),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		enabled: !!id,
	});
}

export function useCreateAssociation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<Association>) => dataService.createAssociation(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.associations.all });
		},
	});
}

export function useUpdateAssociation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<Association> }) =>
			dataService.updateAssociation(id, data),
		onMutate: async ({ id, data }) => {
			await queryClient.cancelQueries({ queryKey: queryKeys.associations.detail(id) });
			const previousData = queryClient.getQueryData(queryKeys.associations.detail(id));
			queryClient.setQueryData(queryKeys.associations.detail(id), (old: Association) =>
				old ? { ...old, ...data } : old,
			);
			return { previousData };
		},
		onError: (err, { id }, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(queryKeys.associations.detail(id), context.previousData);
			}
		},
		onSettled: (data, error, { id }) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.associations.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.associations.lists() });
		},
	});
}

export function useDeleteAssociation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteAssociation(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.associations.all });
		},
	});
}

// Users
export function useUsers(options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.users.list(options),
		queryFn: () => dataService.getUsers(options),
	});
}

export function useUser(id: string) {
	return useQuery({
		queryKey: queryKeys.users.detail(id),
		queryFn: () => dataService.getUser(id),
		enabled: !!id,
	});
}

export function useCreateUser() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<User>) => dataService.createUser(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
		},
	});
}

export function useUpdateUser() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<User> }) =>
			dataService.updateUser(id, data),
		onMutate: async ({ id, data }) => {
			await queryClient.cancelQueries({ queryKey: queryKeys.users.detail(id) });
			const previousData = queryClient.getQueryData(queryKeys.users.detail(id));
			queryClient.setQueryData(queryKeys.users.detail(id), (old: User) =>
				old ? { ...old, ...data } : old,
			);
			return { previousData };
		},
		onError: (err, { id }, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(queryKeys.users.detail(id), context.previousData);
			}
		},
		onSettled: (data, error, { id }) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
		},
	});
}

export function useDeleteUser() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteUser(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
		},
	});
}

// Time Entries
export function useTimeEntries(workOrderId?: string, options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.timeEntries.list(workOrderId, options),
		queryFn: () => dataService.getTimeEntries(workOrderId, options),
	});
}

export function useCreateTimeEntry() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<TimeEntry>) => dataService.createTimeEntry(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
		},
	});
}

export function useUpdateTimeEntry() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<TimeEntry> }) =>
			dataService.updateTimeEntry(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
		},
	});
}

export function useDeleteTimeEntry() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteTimeEntry(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
		},
	});
}

// Materials
export function useMaterials(options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.materials.list(options),
		queryFn: () => dataService.getMaterials(options),
	});
}

export function useCreateMaterial() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<Material>) => dataService.createMaterial(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.materials.all });
		},
	});
}

export function useUpdateMaterial() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<Material> }) => dataService.updateMaterial(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.materials.all });
		},
	});
}

export function useDeleteMaterial() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteMaterial(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.materials.all });
		},
	});
}

// Material Costs
export function useMaterialCosts(workOrderId?: string, options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.materialCosts.list(workOrderId, options),
		queryFn: () => dataService.getMaterialCosts(workOrderId, options),
	});
}

export function useCreateMaterialCost() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<MaterialCost>) => dataService.createMaterialCost(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.materialCosts.all });
		},
	});
}

export function useUpdateMaterialCost() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<MaterialCost> }) =>
			dataService.updateMaterialCost(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.materialCosts.all });
		},
	});
}

export function useDeleteMaterialCost() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteMaterialCost(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.materialCosts.all });
		},
	});
}

// Misc Costs
export function useMiscCosts(workOrderId?: string, options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.miscCosts.list(workOrderId, options),
		queryFn: () => dataService.getMiscCosts(workOrderId, options),
	});
}

export function useCreateMiscCost() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<MiscCost>) => dataService.createMiscCost(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.miscCosts.all });
		},
	});
}

export function useUpdateMiscCost() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<MiscCost> }) =>
			dataService.updateMiscCost(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.miscCosts.all });
		},
	});
}

export function useDeleteMiscCost() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteMiscCost(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.miscCosts.all });
		},
	});
}

// Notes
export function useNotes(workOrderId?: string, options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.notes.list(workOrderId, options),
		queryFn: () => dataService.getNotes(workOrderId, options),
	});
}

export function useCreateNote() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<Note>) => dataService.createNote(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
		},
	});
}

// Attachments
export function useAttachments(workOrderId?: string, inspectionReportId?: string, options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.attachments.list(workOrderId, inspectionReportId, options),
		queryFn: () => dataService.getAttachments(workOrderId, inspectionReportId, options),
	});
}

export function useBillAttachments(billId?: string) {
	return useQuery({
		queryKey: ['attachments', 'bill', billId],
		queryFn: () => dataService.getAttachmentsByBill(billId!),
		enabled: !!billId,
	});
}

export function useInvoiceAttachments(invoiceId?: string) {
	return useQuery({
		queryKey: ['attachments', 'invoice', invoiceId],
		queryFn: () => dataService.getAttachmentsByInvoice(invoiceId!),
		enabled: !!invoiceId,
	});
}

export function usePaymentAttachments(paymentId?: string) {
	return useQuery({
		queryKey: ['attachments', 'payment', paymentId],
		queryFn: () => dataService.getAttachmentsByPayment(paymentId!),
		enabled: !!paymentId,
	});
}

export function useApplicationAttachments(applicationId?: string) {
	return useQuery({
		queryKey: ['attachments', 'application', applicationId],
		queryFn: () => dataService.getAttachmentsByApplication(applicationId!),
		enabled: !!applicationId,
	});
}

export function useCreateAttachment() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<Attachment>) => dataService.createAttachment(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.attachments.all });
		},
	});
}

export function useUpdateAttachment() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<Attachment> }) => dataService.updateAttachment(id, data),
		onSuccess: () => {
			// Invalidate all attachment queries to ensure UI updates correctly
			queryClient.invalidateQueries({ queryKey: queryKeys.attachments.all });
		},
	});
}

export function useDeleteAttachment() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteAttachment(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.attachments.all });
			/**
			 * A message attachment is NOT reachable through `['attachments']`.
			 *
			 * It arrives embedded in the improvement request detail and in the message pane,
			 * so invalidating only the attachment keys left the deleted file on screen with no
			 * feedback at all. The obvious next move is to click Remove again, which 404s and
			 * reports an error for a deletion that in fact succeeded.
			 *
			 * Mirrors `useUpdateAttachmentMetadata`, which already invalidates both for the
			 * same reason.
			 */
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

// Location Events
export function useLocationEvents(userId?: string, workOrderId?: string, options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.locationEvents.list(userId, workOrderId, options),
		queryFn: () => dataService.getLocationEvents(userId, workOrderId, options),
	});
}

export function useCreateLocationEvent() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<LocationEvent>) => dataService.createLocationEvent(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.locationEvents.all });
		},
	});
}

// Inspection Reports
export function useInspectionReports(associationId?: string, options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.inspectionReports.list(associationId, options),
		queryFn: () => dataService.getInspectionReports(associationId, options),
	});
}

export function useCreateInspectionReport() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<InspectionReport>) => dataService.createInspectionReport(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.inspectionReports.all });
		},
	});
}

// Inspection Issues
export function useInspectionIssues(options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.inspectionIssues.list(options),
		queryFn: () => dataService.getInspectionIssues(options),
	});
}

// Inspection Report Issues
export function useInspectionReportIssues(inspectionReportId?: string, options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.inspectionReportIssues.list(inspectionReportId, options),
		queryFn: () => dataService.getInspectionReportIssues(inspectionReportId, options),
	});
}

export function useLocationAnalytics(options: QueryOptions = {}) {
	return useQuery({
		queryKey: queryKeys.locationAnalytics.list(options),
		queryFn: () => dataService.getLocationAnalytics(options),
	});
}

// Tasks
export function useTasks(options: QueryOptions & { enabled?: boolean } = {}) {
	const { enabled, ...queryOptions } = options;
	return useQuery({
		queryKey: queryKeys.tasks.list(queryOptions),
		queryFn: () => dataService.getTasks(queryOptions),
		enabled: enabled !== false,
	});
}

export function useTask(id: string) {
	return useQuery({
		queryKey: queryKeys.tasks.detail(id),
		queryFn: () => dataService.getTask(id),
		enabled: !!id,
	});
}

export function useCreateTask() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<Task>) => dataService.createTask(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
		},
	});
}

export function useUpdateTask() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<Task> }) =>
			dataService.updateTask(id, data),
		onMutate: async ({ id, data }) => {
			await queryClient.cancelQueries({ queryKey: queryKeys.tasks.detail(id) });
			await queryClient.cancelQueries({ queryKey: queryKeys.tasks.lists() });
			const previousDetail = queryClient.getQueryData(queryKeys.tasks.detail(id));
			queryClient.setQueryData(queryKeys.tasks.detail(id), (old: Task) =>
				old ? { ...old, ...data } : old,
			);
			const listQueries = queryClient.getQueriesData<{ data: Task[] }>({ queryKey: queryKeys.tasks.lists() });
			const previousLists = listQueries.map(([key, val]) => [key, val] as const);
			for (const [key] of listQueries) {
				queryClient.setQueryData(key, (old: { data: Task[]; pagination?: unknown } | undefined) => {
					if (!old?.data) return old;
					return { ...old, data: old.data.map((t: Task) => t.id === id ? { ...t, ...data } : t) };
				});
			}
			return { previousDetail, previousLists };
		},
		onError: (err, { id }, context) => {
			if (context?.previousDetail) {
				queryClient.setQueryData(queryKeys.tasks.detail(id), context.previousDetail);
			}
			if (context?.previousLists) {
				for (const [key, val] of context.previousLists) {
					queryClient.setQueryData(key, val);
				}
			}
		},
		onSettled: (data, error, { id }) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
		},
	});
}

export function useDeleteTask() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteTask(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
		},
	});
}

// Follow-ups (#48) — ungated by the tasks_enabled feature flag, so anyone with
// a follow-up assigned to them sees it on their dashboard.
export function useFollowUps() {
	return useQuery<FollowUp[]>({
		queryKey: queryKeys.followUps.all,
		queryFn: () => dataService.getFollowUps(),
	});
}

// Follow-ups for a single work order (all assignees), for the detail page.
export function useWorkOrderFollowUps(workOrderId: string | undefined) {
	return useQuery<FollowUp[]>({
		queryKey: queryKeys.followUps.forWorkOrder(workOrderId || ''),
		queryFn: () => dataService.getFollowUps(workOrderId as string),
		enabled: !!workOrderId,
	});
}

export function useAddFollowUp() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ workOrderId, note }: { workOrderId: string; note?: string }) =>
			dataService.addFollowUp(workOrderId, note),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.followUps.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
		},
	});
}

export function useResolveFollowUp() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (taskId: string) => dataService.resolveFollowUp(taskId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.followUps.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
		},
	});
}

// Calendar Sources & Events
export function useCalendarSources() {
	return useQuery({
		queryKey: queryKeys.calendarSources.all,
		queryFn: () => dataService.getCalendarSources(),
		staleTime: 5 * 60 * 1000,
	});
}

export function useCalendarEvents(timeMin: number, timeMax: number) {
	return useQuery({
		queryKey: queryKeys.calendarEvents.list(timeMin, timeMax),
		queryFn: () => dataService.getCalendarEvents(timeMin, timeMax),
		staleTime: 5 * 60 * 1000,
		enabled: timeMin > 0 && timeMax > 0,
	});
}

export function useUpdateCalendarSource() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CalendarSource> }) =>
			dataService.updateCalendarSource(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.calendarSources.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
		},
	});
}

export function useDeleteCalendarSource() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (sourceId: string) => dataService.deleteCalendarSource(sourceId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.calendarSources.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
		},
	});
}

export function useDisconnectCalendar() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (connectionId: string) => dataService.disconnectCalendar(connectionId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.calendarSources.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
		},
	});
}

export function useSyncCalendars() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => dataService.syncCalendars(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
		},
	});
}

// Exterior Improvement Applications (#363)










/**
 * Website form submissions other than Exterior Improvements (#369). Scoped by
 * account for the account page, or by form type for the per-type inboxes.
 */





/**
 * Owner Questionnaire → contacts (#369). Preview and apply.
 *
 * The preview is a plain query so the pane can show what WOULD change before anyone
 * commits to writing a lot's ownership.
 */
export function useMessageContactPlan(id: string, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: [...queryKeys.messages.all, 'contactPlan', id],
		queryFn: () => dataService.getMessageContactPlan(id),
		enabled: (options.enabled ?? true) && !!id,
	});
}

export function useApplyMessageContacts() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.applyMessageContacts(id),
		onSuccess: () => {
			// The plan itself changes (creates become updates), and the account page's
			// people list is now stale. The accounting hooks key off ['accounting', …]
			// rather than `queryKeys`, so invalidate that root by hand.
			invalidateMessagesAndApplicationCounts(queryClient);
			queryClient.invalidateQueries({ queryKey: ['accounting'] });
		},
	});
}

/** Maintenance Request → a work order (#369). Staff-initiated. */
export function useCreateWorkOrderFromMessage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body?: { priority?: string; workRequested?: string } }) =>
			dataService.createWorkOrderFromMessage(id, body ?? {}),
		onSuccess: () => {
			invalidateMessagesAndApplicationCounts(queryClient);
			queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
		},
	});
}










// Application ruleset admin — shared request types + checklists + letters (#363, Phase 5)
export function useApplicationRuleset() {
	return useQuery({
		queryKey: queryKeys.applicationRulesets.detail(),
		queryFn: () => dataService.getApplicationRuleset(),
	});
}

/**
 * The composed letter for a request.
 *
 * `staleTime: 0` on purpose — it embeds the checklist as it stands, and a cached preview
 * would let someone send a letter naming an item that has since been attached.
 */
export function useNeedsInfoPreview(applicationId: string, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: queryKeys.applications.needsInfoPreview(applicationId),
		queryFn: () => dataService.getNeedsInfoEmailPreview(applicationId),
		enabled: (options.enabled ?? true) && !!applicationId,
		staleTime: 0,
	});
}

export function useSendNeedsInfoEmail() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: NeedsInfoSendBody }) =>
			dataService.sendNeedsInfoEmail(id, body),
		onSuccess: () => {
			// The request's status and stage move, and a message appears on its thread.
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

/** The composed "Waiting for Board Approval" letter. `staleTime: 0` for the same reason. */
export function useBoardApprovalPreview(applicationId: string, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: queryKeys.applications.boardApprovalPreview(applicationId),
		queryFn: () => dataService.getBoardApprovalEmailPreview(applicationId),
		enabled: (options.enabled ?? true) && !!applicationId,
		staleTime: 0,
	});
}

/**
 * Render + stage the application PDF for the composer. Sends nothing, changes nothing — so no
 * cache to invalidate. Not retried: a render is expensive and a timeout usually means slow,
 * not failed; the composer offers its own "Try again".
 */
export function useStageBoardApprovalAttachment() {
	return useMutation({
		mutationFn: (applicationId: string) => dataService.stageBoardApprovalAttachment(applicationId),
		retry: false,
	});
}

export function useSendBoardApprovalEmail() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: BoardApprovalSendBody }) =>
			dataService.sendBoardApprovalEmail(id, body),
		/**
		 * Never retried. The global mutation policy retries a timeout once, and a send is not
		 * idempotent: a first attempt the client gave up on may still have emailed the homeowner.
		 * The server's send claim refuses an overlapping second send, but a retry after the first
		 * finished would be a second letter.
		 */
		retry: false,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

/** Editable letters. `kind` narrows the list; omit for every template. */
export function useEmailTemplates(kind?: string) {
	return useQuery({
		queryKey: queryKeys.emailTemplates.list(kind),
		queryFn: () => dataService.getEmailTemplates(kind),
	});
}

export function useSaveEmailTemplate() {
	const queryClient = useQueryClient();
	return useMutation({
		// One mutation for both verbs: the editor treats "write a default" and "write an
		// override" as the same gesture, and only the presence of an id decides.
		mutationFn: ({ id, data }: { id?: string; data: Partial<EmailTemplate> }) =>
			(id ? dataService.updateEmailTemplate(id, data) : dataService.createEmailTemplate(data)),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.emailTemplates.all });
		},
	});
}

export function useDeleteEmailTemplate() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteEmailTemplate(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.emailTemplates.all });
		},
	});
}

export function useInboxReplyTemplates() {
	return useQuery({
		queryKey: queryKeys.inboxReplyTemplates.list(),
		queryFn: () => dataService.getInboxReplyTemplates(),
	});
}

/**
 * Editing a canned reply changes what the reply COMPOSER offers, so the message detail
 * queries that carry a reply preview are invalidated too. Without that, someone rewords a
 * template and the composer keeps handing out the old text until the tab is reloaded.
 */
function invalidateReplyTemplates(queryClient: ReturnType<typeof useQueryClient>) {
	queryClient.invalidateQueries({ queryKey: queryKeys.inboxReplyTemplates.all });
	queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
}

export function useSaveInboxReplyTemplate() {
	const queryClient = useQueryClient();
	return useMutation({
		// One mutation, both verbs — only the presence of an id decides, matching
		// useSaveEmailTemplate so the editor treats add and edit as the same gesture.
		mutationFn: ({ id, data }: { id?: string; data: Partial<InboxReplyTemplate> }) =>
			(id ? dataService.updateInboxReplyTemplate(id, data) : dataService.createInboxReplyTemplate(data)),
		onSuccess: () => invalidateReplyTemplates(queryClient),
	});
}

export function useDeleteInboxReplyTemplate() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteInboxReplyTemplate(id),
		onSuccess: () => invalidateReplyTemplates(queryClient),
	});
}

// Editing the shared ruleset changes the request-type pickers every association
// sees, so invalidate the exterior-applications tree as well.
function invalidateRulesets(queryClient: ReturnType<typeof useQueryClient>) {
	queryClient.invalidateQueries({ queryKey: queryKeys.applicationRulesets.all });
	queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
}

export function useCreateApplicationRequestType() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: Partial<ApplicationRequestType>) => dataService.createApplicationRequestType(data),
		onSuccess: () => invalidateRulesets(queryClient),
	});
}

export function useUpdateApplicationRequestType() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<ApplicationRequestType> }) =>
			dataService.updateApplicationRequestType(id, data),
		onSuccess: () => invalidateRulesets(queryClient),
	});
}

export function useDeleteApplicationRequestType() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteApplicationRequestType(id),
		onSuccess: () => invalidateRulesets(queryClient),
	});
}


/**
 * The inbox (#369).
 *
 * MEMOIZE the query object at the call site — it is part of the cache key, and an inline
 * literal is a new reference every render, which turns into a refetch loop.
 */
export function useMessages(query: MessageQuery = {}, options: { enabled?: boolean; refetchInterval?: number } = {}) {
	return useQuery({
		queryKey: queryKeys.messages.list(query),
		queryFn: () => dataService.getMessages(query),
		enabled: options.enabled ?? true,
		refetchInterval: options.refetchInterval,
	});
}

/**
 * Messages a page at a time — for a RECORD with no useful cap (#613's account history,
 * where 100+ active messages would otherwise push every archived one off the end).
 * `query.limit` is the PAGE size; the server's order must be total for OFFSET paging.
 * MEMOIZE `query` at the call site; it is the cache key.
 */
export function useMessagePages(query: MessageQuery = {}, options: { enabled?: boolean } = {}) {
	return useInfiniteQuery({
		queryKey: queryKeys.messages.pages(query),
		queryFn: ({ pageParam }) => dataService.getMessages({ ...query, page: pageParam }),
		initialPageParam: 1,
		getNextPageParam: (last) => (last.pagination.page < last.pagination.pages ? last.pagination.page + 1 : undefined),
		enabled: options.enabled ?? true,
	});
}

/** One message, its thread, and what it became. */
export function useMessage(id: string, options: { enabled?: boolean } = {}) {
	return useQuery<InboxMessageDetail>({
		queryKey: queryKeys.messages.detail(id),
		queryFn: () => dataService.getMessage(id),
		enabled: (options.enabled ?? true) && !!id,
	});
}

/**
 * Per-association counts for the grouped rail.
 *
 * Scoped server-side by who is asking, so a property manager gets only their own
 * associations and support gets the whole portfolio grouped by PM.
 */
export function useMessageGroups(
	options: { enabled?: boolean; refetchInterval?: number; unfiledOnly?: boolean } = {},
) {
	const unfiledOnly = options.unfiledOnly ?? false;
	return useQuery<InboxGroups>({
		// In the key: the counts differ between the two views, so they must not share a
		// cache entry — the rail would otherwise show the previous view's numbers.
		queryKey: [...queryKeys.messages.groups(), unfiledOnly],
		queryFn: () => dataService.getMessageGroups(unfiledOnly),
		enabled: options.enabled ?? true,
		refetchInterval: options.refetchInterval,
	});
}

/**
 * The label registry with per-label counts, plus the triage queue size — what the rail
 * renders, and what every inbox badge counts.
 *
 * ── UNFILED BY DEFAULT ──
 *
 * These numbers are OPEN indicators: "this much still needs attention". Mail that has
 * already produced a work order or an improvement request, or that someone reviewed or
 * dismissed, is finished — counting it hands the viewer a number they cannot clear, which
 * is how a badge stops being read.
 *
 * The default is `true` rather than each caller opting in because every consumer of the
 * COUNTS wants it, and the one that does not read counts (the label picker in
 * `message-pane`) is indifferent — the label LIST is returned in full either way, only the
 * counts are filtered. Opting out is therefore an explicit, local decision: `app/inbox/
 * _layout.tsx` passes `!showFiled` so ticking "Show filed" widens the rail and its list
 * together.
 *
 * It defaulted to `false`, and the drawer relied on the default. Its "Assigned to me" badge
 * read 1 while `/inbox/mine` said "Nothing is assigned to you" — the one message it counted
 * had already become an application. A badge that disagrees with the list it opens reads as
 * a broken list.
 */
export function useLabels(
	options: { enabled?: boolean; refetchInterval?: number; unfiledOnly?: boolean } = {},
) {
	const unfiledOnly = options.unfiledOnly ?? true;
	return useQuery<LabelRegistry>({
		// Part of the key for the same reason as useMessageGroups: the two views count
		// different sets and must not share a cache entry.
		queryKey: [...queryKeys.messages.labels(), unfiledOnly],
		queryFn: () => dataService.getLabels(unfiledOnly),
		enabled: options.enabled ?? true,
		refetchInterval: options.refetchInterval,
	});
}

/**
 * Invalidate the messages branch AND the application counts.
 *
 * ── USE THIS FOR EVERY MESSAGE WRITE. ──
 *
 * `/exterior-applications/counts` derives `unconverted` FROM the messages table — an
 * improvement submission counts while it is `status = 'open'`, unlinked, and not yet
 * labelled reviewed/dismissed. That number lives in the APPLICATIONS query branch, the
 * drawer keeps it mounted, and nothing polls it. So a message write that invalidated only
 * `messages.all` left the Exterior Improvements badge counting a submission the Needs
 * Review screen had already dropped — reintroducing, through the cache, exactly the
 * mismatch the server-side `activePredicate` fix removed.
 *
 * It is deliberately applied to EVERY message-write mutation rather than the subset that
 * can provably flip the predicate. Judging it per call site missed `useReplyToMessage`
 * (which archives the original when `archiveAfterSending` is on) and `useRunMessageRule`
 * (rules set labels and status) across two review rounds. The cost of being generous is one
 * small cached GET; the cost of being clever is a badge that lies until the tab refocuses.
 *
 * Application-side mutations do not need it — they invalidate `applications.all`, which
 * already covers `applications.counts()` by key prefix.
 */
function invalidateMessagesAndApplicationCounts(queryClient: ReturnType<typeof useQueryClient>) {
	queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
	queryClient.invalidateQueries({ queryKey: queryKeys.applications.counts() });
	/**
	 * The complaint branch too (#566). Which targets a complaint shows depends on the
	 * message's CURRENT association, so re-routing it must drop the pane's cached chips —
	 * otherwise a chip from the old association stays clickable for the cache lifetime.
	 * A literal rather than `complaintKeys.all`: `use-complaints` imports this module.
	 */
	queryClient.invalidateQueries({ queryKey: ['complaints'] });
	// The board-prep report (keyed under `applications`, see `complaintKeys.boardPrepAll`):
	// archiving or re-routing a complaint moves it between the report's sections.
	queryClient.invalidateQueries({ queryKey: [...queryKeys.applications.all, 'board-prep'] });
}

export function useUpdateMessage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: {
			id: string;
			body: {
				read?: boolean;
				/** @deprecated Send `status` instead; the server maps this to open/archived. */
				archived?: boolean;
				/** Where to move it — `open` | `waiting` | `spam` | `archived`. */
				status?: MessageStatus;
				accountId?: string | null;
				associationId?: string | null; assignedTo?: string | null;
				/** A `WebflowFormType`; the server moves the type LABEL to match (#465). */
				formType?: string;
			};
		}) =>
			dataService.updateMessage(id, body),
		onSuccess: () => invalidateMessagesAndApplicationCounts(queryClient),
	});
}

/**
 * Add or remove a label.
 *
 * Both invalidate the whole messages branch: a label change moves the message between
 * every rail folder AND changes two counts, so a narrower invalidation would leave the
 * rail disagreeing with the list.
 */
export function useSetMessageLabel() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, slug, on }: { id: string; slug: string; on: boolean }) =>
			(on ? dataService.addMessageLabel(id, slug) : dataService.removeMessageLabel(id, slug)),
		onSuccess: () => invalidateMessagesAndApplicationCounts(queryClient),
	});
}

export function useCreateLabel() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: { name: string; color?: string; icon?: string }) => dataService.createLabel(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.messages.labels() });
		},
	});
}

/**
 * The forward composer's preview (#466).
 *
 * `staleTime: 0` deliberately, matching `useNeedsInfoPreview`: the email gate and the
 * Dropbox connection can both change while a tab is open, and a cached "this will really
 * send" is the one answer here that must never be stale.
 */
export function useForwardPreview(id: string, options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [...queryKeys.messages.detail(id), 'forward'],
		queryFn: () => dataService.getForwardPreview(id),
		enabled: (options?.enabled ?? true) && !!id,
		staleTime: 0,
	});
}

/**
 * Send the forward.
 *
 * Invalidates the whole messages branch because the recorded copy is a new message on the
 * thread — the pane's conversation, and the counts, both move.
 */
export function useForwardMessage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: {
			id: string;
			body: Parameters<typeof dataService.forwardMessage>[1];
		}) => dataService.forwardMessage(id, body),
		onSuccess: () => {
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

/**
 * The reply composer's preview (#532).
 *
 * `staleTime: 0` for the same reason the forward preview uses it: the email gate can change
 * while a tab is open, and a cached "this will really send" is the one answer here that
 * must never be stale.
 */
export function useReplyPreview(id: string, options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [...queryKeys.messages.detail(id), 'reply'],
		queryFn: () => dataService.getReplyPreview(id),
		enabled: (options?.enabled ?? true) && !!id,
		staleTime: 0,
	});
}

/**
 * Send the reply.
 *
 * Invalidates the whole messages branch: the recorded copy is a new message on the thread,
 * and "archive after sending" moves the original out of the inbox and every count with it.
 */
export function useReplyToMessage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: {
			id: string;
			body: {
				to: string[]; subject: string; bodyText: string;
				ccSelf: boolean; archiveAfterSending: boolean;
				attachments?: ReplyAttachmentRef[];
			};
		}) => dataService.replyToMessage(id, body),
		onSuccess: () => {
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

/** Inbound-routing rules (admin) — Settings → Assignment Rules. */
export function useMessageRules(options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: ['message-rules'],
		queryFn: () => dataService.getMessageRules(),
		enabled: options?.enabled ?? true,
	});
}

export function useSaveMessageRule() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id?: string; body: any }) =>
			(id ? dataService.updateMessageRule(id, body) : dataService.createMessageRule(body)),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['message-rules'] });
		},
	});
}

/**
 * Apply an existing rule to mail already in the inbox.
 *
 * The LOOP lives here, on the client, because that is the mandated bulk shape in this app
 * (CLAUDE.md): short bounded POSTs the client drives, never a long-lived request. Each chunk
 * is its own round trip, so nothing times out and progress is real rather than simulated.
 *
 * `onProgress` is called after every chunk so the dialog can count up honestly instead of
 * showing a spinner over an unknown amount of work.
 */
export function useRunMessageRule() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, dryRun, onProgress }: {
			id: string;
			dryRun?: boolean;
			onProgress?: (totals: {
				scanned: number; matched: number; applied: number; repliesSuppressed: number;
			}) => void;
		}) => {
			const totals = { scanned: 0, matched: 0, applied: 0, repliesSuppressed: 0 };
			let cursor: string | null = null;
			// Bounded rather than `while (true)`: a cursor bug that stopped advancing would
			// otherwise loop forever against the server. 2,000 chunks × 200 rows is far more
			// than the inbox holds, so hitting this means something is wrong, not that the
			// inbox is big.
			for (let guard = 0; guard < 2000; guard += 1) {
				const chunk = await dataService.runMessageRuleChunk(id, { cursor, dryRun, chunkLimit: 200 });
				totals.scanned += chunk.scanned;
				totals.matched += chunk.matched;
				totals.applied += chunk.applied;
				// Older Workers predate this field; `?? 0` keeps a mid-deploy client honest
				// rather than rendering "NaN letters were not sent".
				totals.repliesSuppressed += chunk.repliesSuppressed ?? 0;
				onProgress?.({ ...totals });
				if (chunk.done || !chunk.cursor) break;
				cursor = chunk.cursor;
			}
			return totals;
		},
		onSuccess: (_totals, variables) => {
			// A dry run wrote nothing, so invalidating would be a pointless refetch of the
			// whole messages branch.
			if (!variables.dryRun) invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

export function useDeleteMessageRule() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.deleteMessageRule(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['message-rules'] });
		},
	});
}

/** File a message's attachments in Dropbox without emailing anyone. */
export function useSaveMessageToDropbox() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.saveMessageToDropbox(id),
		onSuccess: (_result, id) => {
			// The documents panel reads by entity, so its cache is the one that moved.
			queryClient.invalidateQueries({ queryKey: [...queryKeys.messages.detail(id), 'forward'] });
			queryClient.invalidateQueries({ queryKey: ['documents'] });
		},
	});
}


/** Improvement requests (#369). */
export function useApplications(
	query: ApplicationListQuery = {},
	options: { enabled?: boolean } = {},
) {
	return useQuery({
		queryKey: queryKeys.applications.list(query),
		queryFn: () => dataService.getApplications(query),
		enabled: options.enabled ?? true,
	});
}

/**
 * Improvement requests, a page at a time — for a list with no useful cap (#611's
 * per-approver view, whose rail count is uncapped). `query.limit` is the PAGE size.
 * MEMOIZE `query` at the call site; it is the cache key.
 */
export function useApplicationPages(
	query: ApplicationListQuery = {},
	options: { enabled?: boolean } = {},
) {
	return useInfiniteQuery({
		queryKey: queryKeys.applications.pages(query),
		queryFn: ({ pageParam }) => dataService.getApplications({ ...query, page: pageParam }),
		initialPageParam: 1,
		getNextPageParam: (last) => (last.pagination.page < last.pagination.pages ? last.pagination.page + 1 : undefined),
		enabled: options.enabled ?? true,
	});
}

export function useApplication(id: string, options: { enabled?: boolean } = {}) {
	return useQuery<ExteriorApplicationDetail>({
		queryKey: queryKeys.applications.detail(id),
		queryFn: () => dataService.getApplication(id),
		enabled: (options.enabled ?? true) && !!id,
	});
}

/** The type picker's options, honouring per-association overrides. */
export function useRequestTypes(associationId: string, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: queryKeys.applications.requestTypes(associationId),
		queryFn: () => dataService.getRequestTypes(associationId),
		enabled: (options.enabled ?? true) && !!associationId,
		// The ruleset changes maybe once a year; don't re-fetch it per picker open.
		staleTime: 10 * 60 * 1000,
	});
}

/**
 * Start an improvement request by hand (spec §3b). Invalidates the same tree as
 * `useCreateApplicationFromMessage` — `applications.all` covers the account page's list.
 */
export function useCreateApplication() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: NewApplicationBody) => dataService.createApplication(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

/**
 * "This message is an improvement request."
 *
 * Invalidates BOTH branches: a new request appears, and the message gains a link that
 * its /inbox/<id> pane renders as "PART OF".
 */
export function useCreateApplicationFromMessage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: { messageId: string; requestTypeId?: string; accountId?: string }) =>
			dataService.createApplicationFromMessage(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

export function useUpdateApplication() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
			dataService.updateApplication(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
		},
	});
}

/**
 * Free-text account search for the assign-a-lot box (#369).
 *
 * Pass an ALREADY-DEBOUNCED query. `placeholderData` keeps the previous results mounted
 * while the next request is in flight so the dropdown doesn't blank between keystrokes —
 * the stable-search rule in CLAUDE.md.
 */
export function useAccountSearch(query: string, options: { enabled?: boolean } = {}) {
	const trimmed = query.trim();
	return useQuery({
		queryKey: ['accountSearch', trimmed],
		queryFn: () => dataService.searchAccounts(trimmed),
		enabled: (options.enabled ?? true) && trimmed.length >= 2,
		placeholderData: (previous) => previous,
		staleTime: 30_000,
	});
}

/**
 * Link a message onto an existing object — the duplicate-prevention move (#369).
 *
 * Invalidates both branches: the message gains a PART OF entry and the request gains a
 * message, so a narrower invalidation would leave one of the two panes stale.
 */
export function useLinkMessage() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, objectType, objectId }: { id: string; objectType: string; objectId: string }) =>
			dataService.linkMessage(id, objectType, objectId),
		onSuccess: () => {
			invalidateMessagesAndApplicationCounts(queryClient);
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
		},
	});
}

export function useMergeApplication() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, intoId }: { id: string; intoId: string }) => dataService.mergeApplication(id, intoId),
		onSuccess: (_result, { intoId }) => {
			/**
			 * Deliberately NOT `applications.all`. The merged request's detail query is still
			 * mounted — the page the user is standing on — so a blanket invalidate refetches
			 * a record the server just retired and races `router.replace` against a
			 * guaranteed 404. Refresh the lists and the survivor; leave the doomed detail
			 * alone and let the navigation unmount it.
			 */
			queryClient.invalidateQueries({ queryKey: [...queryKeys.applications.all, 'list'] });
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.detail(intoId) });
			// Merging away an approved request soft-deletes it, so its approver's count drops.
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.approvers() });
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

/** Re-pull a message's linked website-form uploads. Safe to press twice. */
export function useFetchMessageLinks() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => dataService.fetchMessageLinks(id),
		onSuccess: (_result, id) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.messages.detail(id) });
		},
	});
}

/**
 * Nav-badge counts for improvement requests.
 *
 * Polled on the same cadence as the rest of the drawer rather than derived from the
 * request LIST — the drawer must not pull 500 rows to draw one number, and it is mounted
 * on every screen in the app.
 */
export function useApplicationCounts(options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: queryKeys.applications.counts(),
		queryFn: () => dataService.getApplicationCounts(),
		enabled: options.enabled ?? true,
	});
}

/** Approved requests per approver — the Approved rail group (#611). */
export function useApplicationApprovers(options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: queryKeys.applications.approvers(),
		queryFn: () => dataService.getApplicationApprovers(),
		enabled: options.enabled ?? true,
	});
}

/** Re-file a photo — type, caption, and which requirement it answers. */
export function useUpdateAttachmentMetadata() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, ...body }: {
			id: string; attachmentType?: string; description?: string | null; checklistKey?: string | null;
		}) => dataService.updateAttachmentMetadata(id, body),
		onSuccess: () => {
			// Both branches: the file moves group AND the checklist it answers changes.
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

/**
 * Attach a file to an improvement request. The bytes go to R2 first (`uploadFile`); this
 * registers the row, anchors it to the request's note message, and mirrors it to the
 * document store.
 */
export function useAddApplicationAttachment() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ applicationId, ...body }: { applicationId: string } & ApplicationAttachmentUpload) =>
			dataService.addApplicationAttachment(applicationId, body),
		onSuccess: () => {
			// The same pair as delete and metadata-edit: the file shows on the REQUEST and on
			// the message pane, and neither is reachable through the `['attachments']` key.
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
			invalidateMessagesAndApplicationCounts(queryClient);
		},
	});
}

/** Add a request type (with its checklist) to the ruleset. Admin-only server-side. */
export function useCreateRequestType() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: {
			name: string;
			slug: string;
			requiredAttachments: Array<{ key: string; label: string; required: boolean }>;
			reviewDeadlineDays?: number;
		}) => dataService.createRequestType(body),
		onSuccess: () => {
			// Every association's picker draws from the same ruleset.
			queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
		},
	});
}
